<template>
  <div class="col-12 grid">
    <div class="card bg-white mb-3" style="border-radius: 10px">
      <div class="card-body">
        <div id="users-grid">
          <h3 class="mt-2">{{ $t("systemUserGrid.systemUsers") }}</h3>
          <BButtonToolbar class="mb-2 pt-2 border-light border-top">
            <BButtonGroup>
              <BButton
                size="sm"
                variant="outline-primary"
                @click="onAddNew"
                v-permission="'AbpIdentity.Users.Create'"
                class="custom-button"
              >
                <font-awesome-icon icon="plus"></font-awesome-icon>
                {{ $t("systemUserGrid.addNew") }}
              </BButton>
              <BButton
                :disabled="datas.selected.length === 0"
                class="ml-2"
                size="sm"
                variant="light"
                @click="onDelete"
                v-permission="'AbpIdentity.Users.Delete'"
                style="background-color: #e9ecef"
              >
                {{ $t("systemUserGrid.delete") }}
              </BButton>
            </BButtonGroup>
            <BPagination
              id="grid-pagination"
              v-model="pagination.page"
              :per-page="pagination.maxResultCount"
              :total-rows="datas.total"
              aria-controls="table"
              class="ml-auto"
              @update:modelValue="onPaginationChange"
            ></BPagination>
          </BButtonToolbar>

          <DataTable
            id="table"
            v-model:sortBy="sort.sortBy"
            :busy="isBusy"
            :fields="fields"
            :items="datas.items"
            :no-local-sorting="true"
            v-model:sort-desc.sync="sort.sortDesc"
            fixed
            hover
            select-mode="single"
            selectable
            @row-selected="onRowSelected"
            @sort-changed="onSortChanged"
            :linkFields="{ email: '/systemUsers/edit/' }"
          >
            <template #table-busy>
              <GridLoading></GridLoading>
            </template>
            <!-- A custom formatted column -->
            <template #cell(email)="data">
              <router-link :to="'/systemUsers/edit/' + data.item.id">{{ data.item.email }}</router-link>
            </template>
          </DataTable>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Inject } from "inversify-props";
import { UserApi } from "@/api";
import { IdentityUserDto, IdentityUserDtoPagedResultDto } from "@/api/model";
import BaseGridMixin from "@/core/views/grids/BaseGrid";
import { AxiosResponse } from "axios";
import GridLoading from "@/core/views/pages/_components/GridLoading.vue";
import { eventBus } from "@/main";
import { mixins, Options } from "vue-class-component";
import { BButton, BButtonGroup, BButtonToolbar, BPagination } from "bootstrap-vue-next";
import DataTable from "@/core/views/pages/_components/Table.vue";

@Options({
  components: { GridLoading, BButton, BButtonGroup, BButtonToolbar, BPagination, DataTable }
})
export default class SystemUsersGrid extends mixins(BaseGridMixin<IdentityUserDto, IdentityUserDtoPagedResultDto>) {
  email: string = "";
  emailConfirmed = "";
  name = "";
  userName = "";
  lockoutEnabled = "";
  lockoutEnd = "";
  id = "";
  fields: any[] = [];
  meta = {
    formRoute: "SystemUserFormCreate"
  };

  @Inject("UserApi")
  private readonly usersApi!: UserApi;

  created(): void {
    this.sort.sortBy = "email";
    eventBus.emit("pageName", "System Users");
    this.email = this.$t("systemUserGrid.email") as string;
    this.emailConfirmed = this.$t("systemUserGrid.emailConfirmed") as string;
    this.name = this.$t("systemUserGrid.name") as string;
    this.userName = this.$t("systemUserGrid.userName") as string;
    this.lockoutEnabled = this.$t("systemUserGrid.lockoutEnabled") as string;
    this.lockoutEnd = this.$t("systemUserGrid.lockoutEnd") as string;
    this.id = this.$t("systemUserGrid.id") as string;
    this.fields = [
      { key: "email", label: this.email, sortable: true },
      { key: "emailConfirmed", label: this.emailConfirmed, sortable: false },
      { key: "name", label: this.name, sortable: true },
      { key: "userName", label: this.userName, sortable: true },
      { key: "lockoutEnabled", label: this.lockoutEnabled, sortable: false },
      { key: "lockoutEnd", label: this.lockoutEnd, sortable: false },
      { key: "id", label: this.id, sortable: false, thStyle: { width: "20%" } }
    ];
  }

  getRequest(
    skipCount?: number,
    maxResultCount?: number,
    sorting?: string
  ): Promise<AxiosResponse<IdentityUserDtoPagedResultDto>> {
    return this.usersApi.apiIdentityUsersGet(undefined, sorting, skipCount, maxResultCount);
  }

  deleteRequest(id: string): Promise<AxiosResponse<void>> {
    return this.usersApi.apiIdentityUsersIdDelete(id);
  }
}
</script>
<style scoped>
.custom-button {
  color: #0054a6;
  background-color: white;
  border-color: #0054a6;
  transition: all 0.02s ease;
  padding: 10px;
}
.custom-button:hover {
  color: white !important;
  background-color: #0054a6 !important;
  border-color: #0054a6;
}
.grid {
  font-size: 14px;
  font-family: Arial, sans-serif;
}
</style>
<style>
@keyframes slideIn {
  from {
    transform: translateY(-100px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes slideOut {
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(-100px);
    opacity: 0;
  }
}

.swal-slide-in {
  animation: slideIn 0.4s ease-out;
}

.swal-slide-out {
  animation: slideOut 0.2s ease-in;
}
.swal2-close {
  color: #636161;
  transition: color 0.2s ease;
  font-size: 2rem;
  width: 2.5rem;
  height: 2.5rem;
}

.swal2-close:hover {
  color: #0f0f0f;
}
</style>
